import { BufferLineQuadTree } from '@/editor/utils/dxf/BufferLineQuadTree';
import type { IDXFRenderContext } from '../types';
import type { DxfBasemapContext } from './types';
import type { TDxfBasemapParserPayload, TDxfBasemapParserResponse, IDxfBasemapRawParserPayload } from './parser.worker';

export interface DxfBasemapParseResult {
  basemapContext: DxfBasemapContext;
  renderContext: IDXFRenderContext;
}

export class DxfBasemapParserService {
  static #instance: DxfBasemapParserService;

  #worker: Worker | null = null;

  private constructor() {}

  public static get instance(): DxfBasemapParserService {
    if (!this.#instance) {
      this.#instance = new DxfBasemapParserService();
    }
    return this.#instance;
  }

  #respawnWorker() {
    this.#terminateWorker();
    this.#worker = new Worker(new URL('./parser.worker.ts', import.meta.url), { type: 'module' });
    return this.#worker;
  }

  #terminateWorker() {
    if (this.#worker) {
      this.#worker.terminate();
      this.#worker = null;
    }
  }

  public async parseRaw(
    buffer: ArrayBuffer,
    options: Pick<IDxfBasemapRawParserPayload, 'transformOcs' | 'renderSolid'>
  ): Promise<DxfBasemapParseResult> {
    const payload: IDxfBasemapRawParserPayload = {
      type: 'parse-raw',
      buffer,
      transformOcs: options.transformOcs
    };

    if (typeof options.renderSolid === 'boolean') {
      payload.renderSolid = options.renderSolid;
    }

    return await this.#run(payload, [buffer]);
  }

  public async buildRenderContext(context: DxfBasemapContext): Promise<DxfBasemapParseResult> {
    return await this.#run({
      type: 'build-context',
      context
    });
  }

  async #run(payload: TDxfBasemapParserPayload, transfer: Transferable[] = []): Promise<DxfBasemapParseResult> {
    const worker = this.#respawnWorker();

    return new Promise<DxfBasemapParseResult>((resolve, reject) => {
      const handleMessage = (event: MessageEvent) => {
        if (!isDxfBasemapParserResponse(event.data)) return;

        worker.removeEventListener('message', handleMessage);
        this.#terminateWorker();

        if (!event.data.success) {
          reject(new Error(event.data.message));
          return;
        }

        const quadTree = BufferLineQuadTree.fromBuffer(event.data.quadTreeBuffer, event.data.context.lineSegArr);
        resolve({
          basemapContext: event.data.basemapContext,
          renderContext: {
            ...event.data.context,
            quadTree
          }
        });
      };

      const handleError = (error: ErrorEvent) => {
        worker.removeEventListener('error', handleError);
        this.#terminateWorker();
        reject(new Error(`Worker error: ${error.message}`));
      };

      worker.addEventListener('message', handleMessage);
      worker.addEventListener('error', handleError, { once: true });
      worker.postMessage(payload, transfer);
    });
  }

  public dispose() {
    this.#terminateWorker();
  }
}

function isDxfBasemapParserResponse(response: unknown): response is TDxfBasemapParserResponse {
  if (typeof response !== 'object' || response === null) return false;

  if (!('success' in response) || typeof response.success !== 'boolean') return false;

  if (!response.success) {
    return 'message' in response && typeof response.message === 'string';
  }

  return (
    'basemapContext' in response &&
    typeof response.basemapContext === 'object' &&
    response.basemapContext !== null &&
    'context' in response &&
    typeof response.context === 'object' &&
    response.context !== null &&
    'quadTreeBuffer' in response &&
    response.quadTreeBuffer instanceof ArrayBuffer
  );
}
